import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Image from 'next/image';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { IExternalSection } from '../../types';
import { CustomLink } from '../link/link.comp';
import { Faq } from '../faq/faq.comp';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaStar } from '@react-icons/all-files/fa/FaStar';
import { FaRegStar } from '@react-icons/all-files/fa/FaRegStar';
import {
	MarkdownStyles,
	StyledExternalPageWrapper,
	StyledSectionWrapper,
	StyledHeroSection,
	StyledComparisonTable,
	StyledStickyButton,
	StyledLogoSlider,
	StyledBannerSection,
	StyledBenefits,
	StyledVideoSection,
	StyledTestimonials,
	StyledContentSection,
	StyledCardsSection,
	StyledFaqSection,
	StyledCustomHtmlSection,
} from './externalSections.styles';

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

interface SectionRendererProps {
	sections: IExternalSection[];
}

// Markdown renderer component
const Markdown = ({ content }: { content?: string }) => {
	if (!content) return null;
	return (
		<MarkdownStyles>
			<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
		</MarkdownStyles>
	);
};

// Hero Sections
const HeroSection = ({ section }: { section: IExternalSection }) => {
	const hasBackground = section.type === 'hero_1_image' && !!section.backgroundUrl;

	// Render hero_1: Simple centered layout
	if (section.type === 'hero_1') {
		return (
			<StyledHeroSection className="hero_1" style={section.style}>
				<div className="hero-content">
					{section.title && <h1 style={section.titleStyle}>{section.title}</h1>}
					{section.subtitle && (
						<h2 style={section.subtitleStyle}>{section.subtitle}</h2>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					<div className="buttons-wrapper">
						{section.buttonText && section.buttonLink && (
							<CustomLink
								href={section.buttonLink}
								target={section.buttonTarget}
								style={section.buttonStyle}
							>
								{section.buttonText}
							</CustomLink>
						)}
					</div>
				</div>
			</StyledHeroSection>
		);
	}

	// Render hero_1_image: Centered with background image
	if (section.type === 'hero_1_image') {
		return (
			<StyledHeroSection
				className="hero_1_image"
				$hasBackground={hasBackground}
				style={{
					...(section.backgroundUrl && {
						backgroundImage: `url(${section.backgroundUrl})`,
					}),
					...section.style,
				}}
			>
				<div className="hero-content">
					{section.title && <h1 style={section.titleStyle}>{section.title}</h1>}
					{section.subtitle && (
						<h2 style={section.subtitleStyle}>{section.subtitle}</h2>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					<div className="buttons-wrapper">
						{section.buttonText && section.buttonLink && (
							<CustomLink
								href={section.buttonLink}
								target={section.buttonTarget}
								style={section.buttonStyle}
							>
								{section.buttonText}
							</CustomLink>
						)}
					</div>
				</div>
			</StyledHeroSection>
		);
	}

	// Render hero_2: Side by side - text left, template preview card right
	if (section.type === 'hero_2') {
		return (
			<StyledHeroSection className="hero_2" style={section.style}>
				<div className="hero-content">
					{section.title && <h1 style={section.titleStyle}>{section.title}</h1>}
					{section.subtitle && (
						<h2 style={section.subtitleStyle}>{section.subtitle}</h2>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					<div className="buttons-wrapper">
						{section.buttonText && section.buttonLink && (
							<CustomLink
								href={section.buttonLink}
								target={section.buttonTarget}
								style={section.buttonStyle}
							>
								{section.buttonText}
							</CustomLink>
						)}
					</div>
				</div>
				{section.imgUrl && (
					<div className="template-preview">
						<div className="preview-image">
							<Image
								src={section.imgUrl}
								alt={section.imgAlt || 'Template preview'}
								fill
								style={{ objectFit: 'cover' }}
							/>
						</div>
						{section.secondaryButtonText && section.secondaryButtonLink && (
							<a
								href={section.secondaryButtonLink}
								target={section.secondaryButtonTarget || '_self'}
								className="preview-button"
							>
								{section.secondaryButtonText}
							</a>
						)}
					</div>
				)}
			</StyledHeroSection>
		);
	}

	// Render hero_3: Side by side - text left, image card right
	if (section.type === 'hero_3') {
		return (
			<StyledHeroSection className="hero_3" style={section.style}>
				<div className="hero-content">
					{section.title && <h1 style={section.titleStyle}>{section.title}</h1>}
					{section.subtitle && (
						<h2 style={section.subtitleStyle}>{section.subtitle}</h2>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					<div className="buttons-wrapper">
						{section.buttonText && section.buttonLink && (
							<CustomLink
								href={section.buttonLink}
								target={section.buttonTarget}
								style={section.buttonStyle}
							>
								{section.buttonText}
							</CustomLink>
						)}
					</div>
				</div>
				{section.imgUrl && (
					<div className="media-wrapper">
						<div className="media-image">
							<Image
								src={section.imgUrl}
								alt={section.imgAlt || 'Hero image'}
								fill
								style={{ objectFit: 'cover' }}
							/>
						</div>
						{section.secondaryButtonText && section.secondaryButtonLink && (
							<a
								href={section.secondaryButtonLink}
								target={section.secondaryButtonTarget || '_self'}
								className="media-button"
							>
								{section.secondaryButtonText}
							</a>
						)}
					</div>
				)}
			</StyledHeroSection>
		);
	}

	// Render hero_4: Side by side - text left, video right
	if (section.type === 'hero_4') {
		return (
			<StyledHeroSection className="hero_4" style={section.style}>
				<div className="hero-content">
					{section.title && <h1 style={section.titleStyle}>{section.title}</h1>}
					{section.subtitle && (
						<h2 style={section.subtitleStyle}>{section.subtitle}</h2>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					<div className="buttons-wrapper">
						{section.buttonText && section.buttonLink && (
							<CustomLink
								href={section.buttonLink}
								target={section.buttonTarget}
								style={section.buttonStyle}
							>
								{section.buttonText}
							</CustomLink>
						)}
					</div>
				</div>
				{section.videoUrl && (
					<div className="media-wrapper">
						<ReactPlayer
							url={section.videoUrl}
							width="100%"
							height="100%"
							controls
							light={section.videoThumbnail || true}
						/>
					</div>
				)}
			</StyledHeroSection>
		);
	}

	// Fallback (shouldn't happen)
	return null;
};

// Comparison Table
const ComparisonTableSection = ({ section }: { section: IExternalSection }) => {
	const sortedItems = [...(section.items || [])].sort(
		(a, b) => (a.order || 0) - (b.order || 0)
	);

	const renderSymbol = (symbol?: string) => {
		if (symbol === 'yes') return <FaCheck className="symbol-yes" />;
		if (symbol === 'no') return <FaTimes className="symbol-no" />;
		return null;
	};

	return (
		<StyledComparisonTable style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.subtitle && (
				<div className="subtitle" style={section.subtitleStyle}>
					<Markdown content={section.subtitle} />
				</div>
			)}
			<div className="table-wrapper" style={section.tableStyle}>
				<div className="table-header">
					<div className="header-cell">Feature</div>
					<div className="header-cell">{section.tableHeader1 || 'Us'}</div>
					<div className="header-cell">
						{section.tableHeader2 || 'Alternative'}
					</div>
				</div>
				{sortedItems.map((item, idx) => (
					<div key={item._id || idx} className="table-row">
						<div className="row-cell">{item.featureName}</div>
						<div className="row-cell">
							{item.rowType === 'symbol'
								? renderSymbol(item.column1Symbol)
								: item.column1Value}
						</div>
						<div className="row-cell">
							{item.rowType === 'symbol'
								? renderSymbol(item.column2Symbol)
								: item.column2Value}
						</div>
					</div>
				))}
			</div>
			{(section.buttonText || section.secondaryButtonText) && (
				<div className="buttons-wrapper">
					{section.buttonText && section.buttonLink && (
						<CustomLink
							href={section.buttonLink}
							target={section.buttonTarget}
							style={section.buttonStyle}
						>
							{section.buttonText}
						</CustomLink>
					)}
					{section.secondaryButtonText && section.secondaryButtonLink && (
						<CustomLink
							href={section.secondaryButtonLink}
							target={section.secondaryButtonTarget}
							variant="secondary"
							style={section.secondaryButtonStyle}
						>
							{section.secondaryButtonText}
						</CustomLink>
					)}
				</div>
			)}
		</StyledComparisonTable>
	);
};

// Sticky Button
const StickyButtonSection = ({ section }: { section: IExternalSection }) => {
	if (!section.buttonText || !section.buttonLink) return null;

	return (
		<StyledStickyButton style={section.style}>
			<a
				href={section.buttonLink}
				target={section.buttonTarget || '_self'}
				style={section.buttonStyle}
			>
				{section.buttonText}
			</a>
		</StyledStickyButton>
	);
};

// Logo Slider
const LogoSliderSection = ({ section }: { section: IExternalSection }) => {
	const items = section.items || [];
	// Duplicate items for infinite scroll effect
	const duplicatedItems = [...items, ...items];

	return (
		<StyledLogoSlider style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			<div className="slider-track">
				{duplicatedItems.map((item, idx) => (
					<div key={`${item._id || idx}-${idx}`} className="logo-item">
						{item.link ? (
							<a
								href={item.link}
								target={item.linkTarget || '_blank'}
								rel="noopener noreferrer"
							>
								{item.imgUrl && (
									<Image
										src={item.imgUrl}
										alt={item.imgAlt || item.title || 'Logo'}
										width={150}
										height={50}
										style={{ objectFit: 'contain' }}
									/>
								)}
							</a>
						) : (
							item.imgUrl && (
								<Image
									src={item.imgUrl}
									alt={item.imgAlt || item.title || 'Logo'}
									width={150}
									height={50}
									style={{ objectFit: 'contain' }}
								/>
							)
						)}
					</div>
				))}
			</div>
		</StyledLogoSlider>
	);
};

// Banner
const BannerSection = ({ section }: { section: IExternalSection }) => {
	return (
		<StyledBannerSection style={section.style}>
			<div className="banner-inner">
				{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
				{section.description && (
					<div className="description" style={section.descriptionStyle}>
						<Markdown content={section.description} />
					</div>
				)}
				<div className="buttons-wrapper">
					{section.buttonText && section.buttonLink && (
						<CustomLink
							href={section.buttonLink}
							target={section.buttonTarget}
							style={section.buttonStyle}
						>
							{section.buttonText}
						</CustomLink>
					)}
					{section.secondaryButtonText && section.secondaryButtonLink && (
						<CustomLink
							href={section.secondaryButtonLink}
							target={section.secondaryButtonTarget}
							variant="secondary"
							style={section.secondaryButtonStyle}
						>
							{section.secondaryButtonText}
						</CustomLink>
					)}
				</div>
			</div>
		</StyledBannerSection>
	);
};

// Benefits
const BenefitsSection = ({ section }: { section: IExternalSection }) => {
	const sortedItems = [...(section.items || [])].sort(
		(a, b) => (a.order || 0) - (b.order || 0)
	);

	return (
		<StyledBenefits style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.subtitle && (
				<div className="subtitle" style={section.subtitleStyle}>
					<Markdown content={section.subtitle} />
				</div>
			)}
			<div className="benefits-grid">
				{sortedItems.map((item, idx) => (
					<div key={item._id || idx} className="benefit-card" style={item.style}>
						{(item.icon || item.imgUrl) && (
							<div className="icon-wrapper">
								{item.imgUrl ? (
									<Image
										src={item.imgUrl}
										alt={item.imgAlt || item.title || 'Benefit icon'}
										width={32}
										height={32}
									/>
								) : (
									<span dangerouslySetInnerHTML={{ __html: item.icon || '' }} />
								)}
							</div>
						)}
						{item.title && <h3>{item.title}</h3>}
						{(item.description || item.text) && (
							<div className="benefit-text">
								<Markdown content={item.description || item.text} />
							</div>
						)}
					</div>
				))}
			</div>
		</StyledBenefits>
	);
};

// Video Section
const VideoSection = ({
	section,
	size,
}: {
	section: IExternalSection;
	size: 'big' | 'small';
}) => {
	return (
		<StyledVideoSection $size={size} style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.subtitle && (
				<div className="subtitle" style={section.subtitleStyle}>
					<Markdown content={section.subtitle} />
				</div>
			)}
			{section.videoUrl && (
				<div className="video-wrapper">
					<ReactPlayer
						url={section.videoUrl}
						width="100%"
						height="100%"
						controls
						light={section.videoThumbnail}
					/>
				</div>
			)}
		</StyledVideoSection>
	);
};

// Testimonials
const TestimonialsSection = ({ section }: { section: IExternalSection }) => {
	const sortedItems = [...(section.items || [])].sort(
		(a, b) => (a.order || 0) - (b.order || 0)
	);

	const renderStars = (rating?: number) => {
		const stars = [];
		const ratingValue = rating || 5;
		for (let i = 1; i <= 5; i++) {
			if (i <= ratingValue) {
				stars.push(<FaStar key={i} className="star" />);
			} else {
				stars.push(<FaRegStar key={i} className="star star-empty" />);
			}
		}
		return stars;
	};

	return (
		<StyledTestimonials style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.subtitle && (
				<div className="subtitle" style={section.subtitleStyle}>
					<Markdown content={section.subtitle} />
				</div>
			)}
			<div className="testimonials-grid">
				{sortedItems.map((item, idx) => (
					<div key={item._id || idx} className="testimonial-card" style={item.style}>
						<div className="rating">{renderStars(item.rating)}</div>
						{(item.text || item.description) && (
							<div className="testimonial-text">
								<Markdown content={item.text || item.description} />
							</div>
						)}
						<div className="author-info">
							{item.authorImage && (
								<Image
									src={item.authorImage}
									alt={item.author || 'Author'}
									width={48}
									height={48}
									className="author-image"
								/>
							)}
							<div className="author-details">
								{item.author && <div className="author-name">{item.author}</div>}
								{item.authorTitle && (
									<div className="author-title">{item.authorTitle}</div>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</StyledTestimonials>
	);
};

// Content Section
const ContentSection = ({
	section,
	hasImage,
}: {
	section: IExternalSection;
	hasImage: boolean;
}) => {
	return (
		<StyledContentSection
			className={!hasImage ? 'no-image' : ''}
			$alignment={section.alignment}
			$position={section.position as 'left' | 'right'}
			$hasImage={hasImage}
			style={section.style}
		>
			<div className="content-wrapper">
				<div className="text-content">
					{section.items?.[0]?.icon && (
						<div className="icon-wrapper">
							<span
								dangerouslySetInnerHTML={{ __html: section.items[0].icon }}
							/>
						</div>
					)}
					{section.items?.[0]?.imgUrl && !hasImage && (
						<div className="icon-wrapper">
							<Image
								src={section.items[0].imgUrl}
								alt={section.items[0].imgAlt || 'Icon'}
								width={28}
								height={28}
							/>
						</div>
					)}
					{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
					{section.subtitle && (
						<h3 style={section.subtitleStyle}>{section.subtitle}</h3>
					)}
					{section.description && (
						<div className="description" style={section.descriptionStyle}>
							<Markdown content={section.description} />
						</div>
					)}
					{(section.buttonText || section.secondaryButtonText) && (
						<div className="buttons-wrapper">
							{section.buttonText && section.buttonLink && (
								<CustomLink
									href={section.buttonLink}
									target={section.buttonTarget}
									style={section.buttonStyle}
								>
									{section.buttonText}
								</CustomLink>
							)}
							{section.secondaryButtonText && section.secondaryButtonLink && (
								<CustomLink
									href={section.secondaryButtonLink}
									target={section.secondaryButtonTarget}
									variant="secondary"
									style={section.secondaryButtonStyle}
								>
									{section.secondaryButtonText}
								</CustomLink>
							)}
						</div>
					)}
				</div>
				{hasImage && section.imgUrl && (
					<figure className="image-wrapper">
						<Image
							src={section.imgUrl}
							alt={section.imgAlt || 'Content image'}
							width={550}
							height={400}
						/>
						{section.imgCaption && (
							<figcaption>{section.imgCaption}</figcaption>
						)}
					</figure>
				)}
			</div>
		</StyledContentSection>
	);
};

// FAQ Section
const FaqSection = ({ section }: { section: IExternalSection }) => {
	const faqs =
		section.items?.map((item) => ({
			q: item.question || item.title || '',
			a: item.answer || item.description || item.text || '',
		})) || [];

	return (
		<StyledFaqSection style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			<Faq faqs={faqs} />
		</StyledFaqSection>
	);
};

// Custom HTML Section
const CustomHtmlSection = ({ section }: { section: IExternalSection }) => {
	return (
		<StyledCustomHtmlSection style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.description && (
				<div
					className="custom-html-content"
					style={section.descriptionStyle}
					dangerouslySetInnerHTML={{ __html: section.description }}
				/>
			)}
			{
				section.customHtml && (
					<div className="custom-html-content" dangerouslySetInnerHTML={{ __html: section.customHtml }} />
				)
			}
		</StyledCustomHtmlSection>
	);
};

// Cards Section (for content with multiple items)
const CardsSection = ({ section }: { section: IExternalSection }) => {
	const items = section.items || [];
	// Determine grid layout based on number of items
	const getGridClass = () => {
		if (items.length === 1) return 'cards-1';
		if (items.length === 2) return 'cards-2';
		return 'cards-3'; // 3 or more items use 3-column grid
	};

	return (
		<StyledCardsSection style={section.style}>
			{section.title && <h2 style={section.titleStyle}>{section.title}</h2>}
			{section.subtitle && (
				<div className="subtitle" style={section.subtitleStyle}>
					<Markdown content={section.subtitle} />
				</div>
			)}
			{section.description && (
				<div className="section-description" style={section.descriptionStyle}>
					<Markdown content={section.description} />
				</div>
			)}
			<div className={`cards-grid ${getGridClass()}`}>
				{items.map((item, idx) => (
					<div key={item._id || idx} className="card" style={item.style}>
						<div className="card-image">
							{item.imgUrl ? (
								<Image
									src={item.imgUrl}
									alt={item.imgAlt || item.title || 'Card image'}
									fill
									style={{ objectFit: 'cover' }}
								/>
							) : (
								<div className="card-image-placeholder" />
							)}
						</div>
						<div className="card-content">
							{item.title && <h3>{item.title}</h3>}
							{item.subtitle && (
								<div className="card-subtitle">{item.subtitle}</div>
							)}
							{(item.description || item.text) && (
								<div className="card-text">
									<Markdown content={item.description || item.text} />
								</div>
							)}
							{
								item.buttonLink && (
									<CustomLink
										href={item.buttonLink}
										target={item.buttonTarget || section.buttonTarget || '_self'}
									>
										{item.buttonText}
									</CustomLink>
								)
							}
						</div>
					</div>
				))}
			</div>
		</StyledCardsSection>
	);
};

// Helper to determine if section needs full-width wrapper
const isFullWidthSection = (type: string) => {
	return ['logo_slider', 'testimonials', 'sticky_button', 'hero_1_image', 'custom_html'].includes(type);
};

// Main Section Renderer
export const ExternalSectionRenderer = ({ sections }: SectionRendererProps) => {
	// Filter invisible sections but keep original order (don't sort!)
	const visibleSections = sections.filter(
		(section) => section.isVisible !== false
	);

	// Track content section alternation
	let contentIndex = 0;

	const renderSection = (section: IExternalSection, idx: number) => {
		const key = section._id || `section-${idx}`;

		switch (section.type) {
			case 'hero_1':
			case 'hero_1_image':
			case 'hero_2':
			case 'hero_3':
			case 'hero_4':
				return <HeroSection key={key} section={section} />;

			case 'comparison_table':
				return <ComparisonTableSection key={key} section={section} />;

			case 'sticky_button':
				return <StickyButtonSection key={key} section={section} />;

			case 'logo_slider':
				return <LogoSliderSection key={key} section={section} />;

			case 'banner':
				return <BannerSection key={key} section={section} />;

			case 'benefits':
				return <BenefitsSection key={key} section={section} />;

			case 'video_big':
				return <VideoSection key={key} section={section} size="big" />;

			case 'video_small':
				return <VideoSection key={key} section={section} size="small" />;

			case 'testimonials':
				return <TestimonialsSection key={key} section={section} />;

			case 'cards':
				return <CardsSection key={key} section={section} />;

			case 'content': {
				// Alternate position for content sections with images
				const position =
					section.position || (contentIndex % 2 === 0 ? 'left' : 'right');
				contentIndex++;
				return (
					<ContentSection
						key={key}
						section={{ ...section, position }}
						hasImage={true}
					/>
				);
			}

			case 'content_no_image':
				return (
					<ContentSection key={key} section={section} hasImage={false} />
				);

			case 'faq':
				return <FaqSection key={key} section={section} />;

			case 'custom_html':
				return <CustomHtmlSection key={key} section={section} />;

			default:
				// Log unknown section types in development
				if (process.env.NODE_ENV === 'development') {
					console.warn(`Unknown section type: ${section.type}`, section);
				}
				return null;
		}
	};

	// Check if any section has custom HTML that might need Common Ninja SDK
	const hasCustomHtml = visibleSections.some(
		(section) => section.type === 'custom_html'
	);

	return (
		<StyledExternalPageWrapper>
			{hasCustomHtml && (
				<Script
					src="https://cdn.commoninja.com/sdk/latest/commonninja.js"
					strategy="lazyOnload"
				/>
			)}
			{visibleSections.map((section, idx) => {
				const isFullWidth = isFullWidthSection(section.type);
				const isStickyButton = section.type === 'sticky_button';

				// Sticky button doesn't need a wrapper
				if (isStickyButton) {
					return renderSection(section, idx);
				}

				return (
					<StyledSectionWrapper
						key={section._id || `wrapper-${idx}`}
						className={isFullWidth ? 'full-width' : ''}
					>
						{renderSection(section, idx)}
					</StyledSectionWrapper>
				);
			})}
		</StyledExternalPageWrapper>
	);
};
